// ----------------------------------------------------------------------

// Genuine pixel-art stepped corners (not just square) — a notch cut from
// each corner via clip-path. calc() keeps this generic across any button
// size, unlike react-pixel-ui's approach of measuring each element in JS.
const STEP = 6; // px — corner notch size
const PIXEL_CLIP_PATH = `polygon(
  ${STEP}px 0, calc(100% - ${STEP}px) 0,
  calc(100% - ${STEP}px) ${STEP}px, 100% ${STEP}px,
  100% calc(100% - ${STEP}px), calc(100% - ${STEP}px) calc(100% - ${STEP}px),
  calc(100% - ${STEP}px) 100%, ${STEP}px 100%,
  ${STEP}px calc(100% - ${STEP}px), 0 calc(100% - ${STEP}px),
  0 ${STEP}px, ${STEP}px ${STEP}px
)`;

const OUTLINE = 2; // px — outline thickness
const SHADOW = 6; // px — cast-shadow offset

// A transparent border reserves a ring of space; layering the fill as a
// `background-image` clipped to the padding-box (leaving a plain-color
// `background-image` clipped to the border-box beneath it) fills that ring
// with solid ink. This clips cleanly through the stepped corners in one
// pass — unlike chaining multiple `drop-shadow()`s for an outline, which
// compounds (each shadow also shadows the previous one) into visible
// ghost lines.
const pixelFill = (ink, fill) => `${fill} padding-box, linear-gradient(${ink}, ${ink}) border-box`;

// Contained buttons: ink border ring + solid cast shadow, pressed flush
// (shadow removed, button translated into the space it vacated) on `:active`.
const pixelContained = (ink, fill) => ({
  clipPath: PIXEL_CLIP_PATH,
  border: `${OUTLINE}px solid transparent`,
  background: pixelFill(ink, fill),
  boxShadow: 'none',
  filter: `drop-shadow(${SHADOW}px ${SHADOW}px 0 ${ink})`,
  transition: 'filter 0.1s ease, transform 0.1s ease',
  '&:active': {
    filter: 'none',
    transform: `translate(${SHADOW}px, ${SHADOW}px)`
  }
});

// Outlined/text buttons: stepped shape + a plain border only, no cast
// shadow — keeps secondary actions visually lighter than primary CTAs.
const pixelOutlined = (color) => ({
  clipPath: PIXEL_CLIP_PATH,
  border: `${OUTLINE}px solid ${color}`
});

export default function Button(theme) {
  const INK = theme.palette.pixelInk;

  return {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          letterSpacing: 0.8
        },
        sizeLarge: {
          height: 52,
          fontSize: '0.9375rem'
        },
        containedInherit: {
          color: theme.palette.grey[800],
          ...pixelContained(INK, `linear-gradient(${theme.palette.grey[200]}, ${theme.palette.grey[200]})`),
          '&:hover': { filter: `drop-shadow(${SHADOW}px ${SHADOW}px 0 ${INK}) brightness(0.97)` }
        },
        containedPrimary: pixelContained(INK, theme.palette.gradients.primary),
        containedSecondary: pixelContained(INK, theme.palette.gradients.info),
        containedInfo: pixelContained(INK, theme.palette.gradients.info),
        containedSuccess: pixelContained(INK, theme.palette.gradients.success),
        containedWarning: pixelContained(INK, theme.palette.gradients.warning),
        containedError: pixelContained(INK, theme.palette.gradients.error),
        outlinedPrimary: pixelOutlined(theme.palette.primary.main),
        outlinedSecondary: pixelOutlined(theme.palette.secondary.main),
        outlinedInherit: {
          ...pixelOutlined(INK),
          '&:hover': { backgroundColor: theme.palette.action.hover }
        },
        textInherit: {
          '&:hover': { backgroundColor: theme.palette.action.hover }
        }
      }
    }
  };
}
